/**
 * KPI Widget (Story 5.5 → Epic 37 redesign — clean stat card)
 *
 * Value-first hierarchy (research-backed): the big neutral value leads; the delta
 * is a colored "verdict" pill below it (color on the delta, never the value;
 * arrow always paired with color for accessibility). Title + size-aware chrome
 * come from the shared chart-widget shell, so the canvas widget and the sidebar
 * preview render identically. Formatting/sizing/trend stay on the shared
 * report-rendering functions for PDF parity.
 */

import type { ReactNode } from "react";
import type { WidgetConfig } from "../../../lib/templateUtils";
import type { KpiData, WidgetDataEnvelope, BenchmarkComparison } from "../../../lib/widgetData";
import { isKpiData } from "../../../lib/widgetData";
import { TREND_COLORS } from "../../../lib/chartConstants";
import {
  formatKpiValue,
  getKpiFontSize,
  calculateTrend,
  formatBenchmarkMetricValue,
} from "@provenleap/types/report-rendering";
import { Info, ArrowUp, ArrowDown, Minus } from "@phosphor-icons/react";
import { useNavigate } from "@tanstack/react-router";
import { Button } from "@provenleap/ui";
import { usePlanLimits } from "../../../hooks/usePlanLimits";
import { useChartWidgetShell, ChartMessage } from "./chartWidgetShell";

// Re-export for backward compatibility (tests import from this file)
export { getKpiFontSize } from "@provenleap/types/report-rendering";

interface KpiWidgetProps {
  widget: WidgetConfig;
  data?: WidgetDataEnvelope<KpiData>;
}

/** Story 35.5 — humanize a Meta per-attribution-window metric key suffix. */
function getAttributionWindowSubtitle(metricKey: string | undefined): string | null {
  if (!metricKey) return null;
  if (metricKey.endsWith("_1d_click")) return "1-day click attribution";
  if (metricKey.endsWith("_7d_click")) return "7-day click attribution";
  if (metricKey.endsWith("_28d_click")) return "28-day click attribution";
  if (metricKey.endsWith("_1d_view")) return "1-day view attribution";
  return null;
}

/** Delta verdict pill — color on the delta (not the value) + arrow (a11y). */
function TrendIndicator({ direction, percentage }: { direction: "up" | "down" | "neutral"; percentage: number }) {
  const color = TREND_COLORS[direction];
  const arrow = direction === "up" ? "↑" : direction === "down" ? "↓" : "→";
  return (
    <span
      role="status"
      className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[12px] font-semibold leading-none whitespace-nowrap"
      style={{ color, backgroundColor: `color-mix(in srgb, ${color} 12%, transparent)` }}
      aria-label={`Trend ${direction} ${percentage.toFixed(1)}%`}
    >
      <span aria-hidden>{arrow}</span>
      <span>{percentage.toFixed(1)}%</span>
    </span>
  );
}

/** Tiny in-card trend line (no axes/labels) — the KPI sparkline. Normalises the
 *  series into a fixed viewBox and draws a polyline (+ a soft area fill). */
function Sparkline({ points, color, width = 120, height = 26 }: { points: number[]; color: string; width?: number; height?: number }) {
  if (points.length < 2) return null;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const span = max - min || 1;
  const pad = 2;
  const innerW = width - pad * 2;
  const innerH = height - pad * 2;
  const coords = points.map((v, i) => {
    const x = pad + (i / (points.length - 1)) * innerW;
    const y = pad + (1 - (v - min) / span) * innerH; // higher value = higher on screen
    return [x, y] as const;
  });
  const line = coords.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const area = `${pad},${height - pad} ${line} ${(width - pad).toFixed(1)},${height - pad}`;
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" data-testid="kpi-sparkline" aria-hidden>
      <polygon points={area} fill={color} opacity={0.12} />
      <polyline points={line} fill="none" stroke={color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ── Inline industry benchmark (Story 8.11) ───────────────────────────
//
// Colorblind-safe badge palette MIRRORS BenchmarkWidget's BADGE_CONFIG (blue /
// gray / amber + directional Phosphor icons — NOT green/gray/orange, which
// fails deuteranopia). Kept in lock-step with that widget on purpose.
type BenchmarkBadgeLevel = "above" | "average" | "below";
const BENCHMARK_BADGE_CONFIG: Record<
  BenchmarkBadgeLevel,
  { label: string; tailwind: string; Icon: typeof ArrowUp }
> = {
  above: { label: "Above Average", tailwind: "text-blue-600", Icon: ArrowUp },
  average: { label: "Average", tailwind: "text-gray-500", Icon: Minus },
  below: { label: "Below Average", tailwind: "text-amber-500", Icon: ArrowDown },
};

/**
 * Benchmark data older than 12 months gets a subtle provenance note.
 *
 * Primary signal is the `lastUpdated` timestamp (a true >12-month test). The
 * effectiveYear arm is only a FALLBACK for rows missing a real lastUpdated
 * (0/absent) — and it uses `>= 1` so last-year data on this year's reads is
 * flagged stale (the old `> 1` only tripped at 2+ years behind, so 2025 data
 * read fresh in 2026 — Round-1 review F2).
 */
function isBenchmarkStale(cmp: BenchmarkComparison): boolean {
  const TWELVE_MONTHS_MS = 365 * 24 * 60 * 60 * 1000;
  if (cmp.lastUpdated) {
    return Date.now() - cmp.lastUpdated > TWELVE_MONTHS_MS;
  }
  // Fallback when lastUpdated is missing/0: lean on the effective year.
  return new Date().getFullYear() - cmp.effectiveYear >= 1;
}

/** "Industry avg: {median}" + relative-performance badge + freshness note. */
function InlineBenchmark({ cmp, compact }: { cmp: BenchmarkComparison; compact: boolean }) {
  const badge = BENCHMARK_BADGE_CONFIG[cmp.badge];
  return (
    <div
      className={`flex flex-col items-center gap-0.5 ${compact ? "mt-1.5" : "mt-2"}`}
      data-testid="kpi-benchmark"
    >
      <span className="text-[11px] tabular-nums text-muted-foreground" data-testid="kpi-benchmark-median">
        Industry avg: {formatBenchmarkMetricValue(cmp.median, cmp.unit)}
      </span>
      <span
        className={`inline-flex items-center gap-1 text-[11px] font-medium ${badge.tailwind}`}
        data-testid={`kpi-benchmark-badge-${cmp.badge}`}
      >
        <badge.Icon size={12} weight="bold" aria-hidden />
        {badge.label}
      </span>
      {isBenchmarkStale(cmp) && (
        <span className="text-[10px] text-muted-foreground/80" data-testid="kpi-benchmark-stale">
          Benchmark data from {cmp.source}, {cmp.effectiveYear}
        </span>
      )}
    </div>
  );
}

/** Free-plan CTA shown when showBenchmark is on but the tenant can't see data. */
function BenchmarkUpgradeCta() {
  const navigate = useNavigate();
  return (
    <div className={`mt-2 flex flex-col items-center gap-1`} data-testid="kpi-benchmark-cta">
      <Button
        variant="outline"
        size="sm"
        onClick={() =>
          navigate({ to: "/settings/billing", search: { upgrade: "true", plan: "starter" } })
        }
      >
        See how you compare to your industry
      </Button>
    </div>
  );
}

export function KpiWidget({ widget, data }: KpiWidgetProps) {
  const config = widget.config as {
    metricKey?: string;
    comparisonEnabled?: boolean;
    showPrivacyDisclaimer?: boolean;
    showConversionRateDisclaimer?: boolean;
    showBenchmark?: boolean;
  };
  const { plan } = usePlanLimits();
  const colSpan = widget.position.colSpan;
  const isNarrow = colSpan <= 2;
  // Appearance > Text color / Font / Text size — override the value's defaults.
  const appearance = widget.config?.style as { textColor?: string; fontFamily?: string; fontSize?: number; hideSparkline?: boolean } | undefined;
  const textColor = appearance?.textColor;
  const fontFamily = appearance?.fontFamily;
  const customValueSize = typeof appearance?.fontSize === "number" ? appearance.fontSize : undefined;
  const hideSparkline = appearance?.hideSparkline === true;
  const { compact, shell } = useChartWidgetShell(widget.config);

  const attributionSubtitle = getAttributionWindowSubtitle(config.metricKey);

  // Build the title node (with subtitle + disclaimer) — passed as the shell title.
  // The shared shell only takes a string title, so for the KPI's richer header we
  // render value-state internals and keep the title plain; the subtitle/disclaimer
  // render inside the body to stay addressable by their test ids.
  const shellBody = (body: ReactNode) => shell("kpi", widget.title, body, { centered: true });

  if (data?.loading) return shellBody(<div className="h-8 w-24 animate-pulse rounded bg-muted" />);
  if (data?.error) return shellBody(<ChartMessage role="alert">{data.error}</ChartMessage>);

  const kpiData = data?.data;
  if (!kpiData || !isKpiData(kpiData)) {
    return shellBody(<span className="text-sm text-muted-foreground" data-testid="kpi-no-data">{"—"}</span>);
  }

  const formattedValue = formatKpiValue(kpiData.value, kpiData.format);
  const fontSize = getKpiFontSize(formattedValue, colSpan);
  const trend = kpiData.previousValue != null ? calculateTrend(kpiData.value, kpiData.previousValue) : null;

  const disclaimer =
    config.showPrivacyDisclaimer && !config.showConversionRateDisclaimer ? (
      <span className="text-muted-foreground" title="Open rates may be inflated by Apple Mail Privacy Protection" data-testid="kpi-privacy-disclaimer">
        <Info size={14} />
      </span>
    ) : config.showConversionRateDisclaimer ? (
      <span className="text-muted-foreground" title="Based on Shopify sessions. May differ from Google Analytics." data-testid="kpi-conversion-rate-disclaimer">
        <Info size={14} />
      </span>
    ) : null;

  return shellBody(
    // Inline-styled centering (immune to any CSS-utility gap): fill the body and
    // center the value block both axes. Disclaimer pinned to the corner.
    <div
      style={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        height: "100%",
        width: "100%",
      }}
    >
      {disclaimer && <div className="absolute right-0 top-0">{disclaimer}</div>}
      {attributionSubtitle && !compact && (
        <div className="mb-1 max-w-full truncate text-[10px] text-muted-foreground/80" data-testid="kpi-attribution-window" title={attributionSubtitle}>
          {attributionSubtitle}
        </div>
      )}
      <div
        className="max-w-full truncate font-semibold text-foreground"
        // Display the value larger than the shared base size (which is tuned for
        // PDF density) so the number reads as the hero on-screen. 1.5× of the
        // shared getKpiFontSize keeps PDF parity logic untouched.
        style={{ fontSize: `${customValueSize ?? Math.round(fontSize * 1.5)}px`, lineHeight: 1.05, letterSpacing: "-0.02em", textAlign: "center", ...(textColor ? { color: textColor } : {}), ...(fontFamily ? { fontFamily } : {}) }}
        data-testid="kpi-value"
        title={formattedValue}
      >
        {formattedValue}
      </div>
      {/* Sparkline — the in-card trend line (anatomy layer 4). Shown whenever a
          series exists; shorter on small (compact) cards so it still fits. Coloured
          by trend direction. Hidden via Appearance > "Show trend line". */}
      {!hideSparkline && kpiData.sparkline && kpiData.sparkline.length > 1 && (
        <div className={`w-full max-w-[160px] px-2 ${compact ? "mt-1" : "mt-2"}`} data-testid="kpi-sparkline-wrap">
          <Sparkline
            points={kpiData.sparkline}
            color={trend ? TREND_COLORS[trend.direction] : TREND_COLORS.neutral}
            height={compact ? 16 : 26}
          />
        </div>
      )}
      {trend && (
        <div className="mt-2 flex items-center justify-center gap-2" data-testid="kpi-trend">
          <TrendIndicator direction={trend.direction} percentage={trend.percentage} />
          {!isNarrow && !compact && config.comparisonEnabled && (
            <span className="whitespace-nowrap text-[11px] text-muted-foreground">vs. previous period</span>
          )}
        </div>
      )}
      {/* Story 8.11 — inline industry benchmark. The server attaches
          benchmarkComparison only when all gates pass (incl. Starter+). When the
          toggle is on but the tenant is Free, the server omits it and the web
          shows the upgrade CTA. When on but nothing resolves (industry/metric/
          platform miss), render nothing (graceful). */}
      {kpiData.benchmarkComparison ? (
        <InlineBenchmark cmp={kpiData.benchmarkComparison} compact={compact} />
      ) : config.showBenchmark && plan === "free" ? (
        <BenchmarkUpgradeCta />
      ) : null}
    </div>,
  );
}
